import { useEffect, useState } from "react";
import { RecentDraws, Flag } from "./LiveDrawResults";
import { imgSrc } from "./TvPromoManager";
import { themeStyle, bgStyle } from "../utils/brandTheme";
import { Maximize, MessageCircle, Smartphone, Megaphone } from "lucide-react";

const REFRESH_MS = 120000;
const ROTATE_MS = 15000;
const PROMO_MS = 10000;

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="font-mono">{now.toLocaleTimeString("es-CR", { timeZone: "America/Costa_Rica", hour: "2-digit", minute: "2-digit" })}</span>;
};

const TvCountry = ({ group }) => (
  <div className="grid grid-cols-4 gap-4" key={group.country} style={{ animation: "fadeInUp 0.5s ease both" }}>
    {group.draws.slice(-12).map((d, i) => (
      <div key={`${d.name}-${i}`} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xl font-semibold text-white leading-tight line-clamp-2">{d.name}</p>
          <span className="text-xl font-mono text-brand shrink-0">{d.time}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {d.numbers.map((n, j) => (
            <span key={j} className={`min-w-[56px] h-[56px] px-2 rounded-full flex items-center justify-center font-black font-mono text-2xl ${j === 0 ? "bg-brand text-on-brand" : "bg-white/15 text-white"}`}>{n}</span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const TvPromo = ({ item }) => (
  <div className="grid grid-cols-5 gap-10 items-center rounded-3xl bg-brand-gradient px-10 py-8 text-on-brand" data-testid="tv-promo-slide"
    style={{ animation: "fadeInUp 0.5s ease both" }}>
    <div className="col-span-2 h-[360px] rounded-2xl bg-white/30 overflow-hidden flex items-center justify-center">
      {item.image_url ? <img src={imgSrc(item.image_url)} alt={item.title} className="w-full h-full object-cover" /> : <Megaphone className="w-40 h-40 opacity-40" />}
    </div>
    <div className="col-span-3 space-y-5">
      <span className="inline-block rounded-full bg-slate-900 text-brand px-5 py-2 text-xl font-bold uppercase tracking-wider">
        {item.kind === "product" ? "Producto destacado" : "Promoción"}
      </span>
      <p className="text-6xl font-black leading-none line-clamp-2" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{item.title}</p>
      {item.text && <p className="text-3xl font-semibold opacity-80 line-clamp-2">{item.text}</p>}
      {item.price && <p className="text-5xl font-black font-mono">{item.price}</p>}
    </div>
  </div>
);

export const TvBoard = ({ fetcher, promoFetcher, settings }) => {
  const { brand_name, whatsapp, sinpe } = settings;
  const [data, setData] = useState(null);
  const [promos, setPromos] = useState([]);
  const [step, setStep] = useState(0);
  const [isFull, setIsFull] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const load = () => {
      fetcher("hoy").then((r) => setData(r.data)).catch(() => {});
      promoFetcher?.().then((r) => setPromos(r.data.items || [])).catch(() => {});
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [fetcher, promoFetcher]);

  const groups = data?.groups || [];
  const slides = groups.flatMap((g, i) => promos.length ? [{ type: "country", g }, { type: "promo", p: promos[i % promos.length] }] : [{ type: "country", g }]);
  const slide = slides[step % (slides.length || 1)];
  const duration = slide?.type === "promo" ? PROMO_MS : ROTATE_MS;

  useEffect(() => {
    if (!slides.length) return;
    const t = setTimeout(() => setStep((s) => (s + 1) % slides.length), duration);
    return () => clearTimeout(t);
  }, [step, slides.length, duration]);

  useEffect(() => {
    const h = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const current = slide?.type === "country" ? slide.g : null;
  const idx = current ? groups.indexOf(current) : -1;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8 flex flex-col gap-6" style={{ ...themeStyle(settings), ...bgStyle(settings, "rgba(2,6,23,0.82)") }} data-testid="tv-board">
      <header className="flex items-center justify-between">
        <p className="text-5xl font-black tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }} data-testid="tv-brand">{brand_name}</p>
        <div className="flex items-center gap-4 text-2xl font-semibold">
          {whatsapp && <span className="flex items-center gap-2 rounded-full bg-emerald-600 px-6 h-14"><MessageCircle className="w-7 h-7" /> WhatsApp {whatsapp}</span>}
          {sinpe && <span className="flex items-center gap-2 rounded-full bg-white/10 px-6 h-14"><Smartphone className="w-7 h-7 text-brand" /> Sinpe {sinpe}</span>}
          <span className="text-4xl text-brand ml-4" data-testid="tv-clock"><Clock /></span>
        </div>
      </header>

      {!data && <p className="text-3xl text-white/60 text-center py-20">Consultando resultados…</p>}
      <RecentDraws groups={groups} tv />

      {slide?.type === "promo" && <TvPromo item={slide.p} />}

      {current && (
        <section className="flex-1 flex flex-col gap-4" data-testid="tv-country">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-3 text-4xl font-bold">
              <Flag country={current.country} className="w-14 h-10" /> {current.country}
            </h2>
            <div className="flex gap-2">
              {groups.map((g, i) => (
                <span key={g.country} className={`h-3 rounded-full transition-all ${i === idx ? "w-10 bg-brand" : "w-3 bg-white/25"}`} />
              ))}
            </div>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div key={`${step}-${current.country}`} className="h-full bg-brand" style={{ animation: `tvProgress ${ROTATE_MS}ms linear both` }} />
          </div>
          <TvCountry group={current} />
        </section>
      )}

      {!isFull && (
        <button onClick={() => document.documentElement.requestFullscreen?.()} data-testid="tv-fullscreen"
          className="fixed bottom-6 right-6 flex items-center gap-2 rounded-full bg-brand text-on-brand font-bold px-6 h-14 text-lg shadow-xl hover:opacity-90 transition-colors">
          <Maximize className="w-5 h-5" /> Pantalla completa
        </button>
      )}
    </div>
  );
};
